import React, { useState, useEffect } from 'react';
import { missionService } from '../services/api';
import { useTranslation } from 'react-i18next';
import { getLocalizedContent } from '../utils/language';
import toast from 'react-hot-toast';

function AssignmentManager({ missionId, missionTitle, isOpen, onClose, onAssignmentsUpdated }) {
  const { t, i18n } = useTranslation();

  // Segona barrera: si algun dia torna a arribar el títol en cru —{en, ca}—,
  // escriure'l al JSX no ha de tombar la pàgina sencera. És exactament el que
  // passava fins al 06/08/2026 en obrir aquesta finestra des de la pantalla de
  // la missió, que demana el contingut sense traduir per poder-lo editar.
  const titol = typeof missionTitle === 'string'
    ? missionTitle
    : getLocalizedContent(missionTitle, i18n.language);
  const [assignments, setAssignments] = useState({
    assigned_users: [],
    available_users: []
  });
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAssignments();
    }
  }, [isOpen, missionId]);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await missionService.getAssignments(missionId);
      setAssignments(response.data);

      // Set initially selected users (currently assigned)
      const assignedIds = new Set(response.data.assigned_users.map(u => u.id));
      setSelectedUsers(assignedIds);
    } catch (error) {
      toast.error(t('assignmentManager.errorLoadingAssignments'));
      console.error('Assignment fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    const allUserIds = new Set(assignments.available_users.map(u => u.id));
    setSelectedUsers(allUserIds);
  };

  const handleSelectNone = () => {
    setSelectedUsers(new Set());
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const userIds = Array.from(selectedUsers);
      await missionService.bulkAssignUsers(missionId, userIds);

      toast.success(t('assignmentManager.assignmentsUpdatedSuccess'));
      onAssignmentsUpdated?.();
      onClose();
    } catch (error) {
      let message = t('assignmentManager.errorUpdatingAssignments');
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const getAssignmentChanges = () => {
    const currentlyAssigned = new Set(assignments.assigned_users.map(u => u.id));
    const newAssignments = selectedUsers;

    const toAdd = Array.from(newAssignments).filter(id => !currentlyAssigned.has(id));
    const toRemove = Array.from(currentlyAssigned).filter(id => !newAssignments.has(id));

    return { toAdd, toRemove };
  };

  const { toAdd, toRemove } = getAssignmentChanges();
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  // Els equips primer, i per nom visible.
  //
  // Assignar la missió del dia és la darrera cosa que es fa abans d'un examen, i
  // a l'examen no tots els equips fan la mateixa missió: cal poder triar «Equip
  // 01 a 06» dins d'una llista on també hi ha quaranta comptes de persona d'un
  // curs sencer. Ordenats per nom d'usuari, «equip-07» queda entremig de la e.
  const perTriar = [...assignments.available_users].sort((a, b) => {
    if (!!a.es_equip !== !!b.es_equip) return a.es_equip ? -1 : 1;
    return (a.display_name || a.username).localeCompare(b.display_name || b.username);
  });

  const nomDe = (userId) => {
    const user = assignments.available_users.find(u => u.id === userId);
    return user?.display_name || user?.username;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content assignment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('assignmentManager.manageAssignments')} - {titol}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">{t('assignmentManager.loadingAssignments')}</div>
          ) : (
            <>
              <div className="assignment-summary">
                <div className="summary-item">
                  <span className="summary-label">{t('assignmentManager.totalStudents')}:</span>
                  <span className="summary-value">{assignments.available_users.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">{t('assignmentManager.currentlyAssigned')}:</span>
                  <span className="summary-value">{assignments.assigned_users.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">{t('assignmentManager.selected')}:</span>
                  <span className="summary-value">{selectedUsers.size}</span>
                </div>
              </div>

              <div className="assignment-controls">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleSelectAll}
                  disabled={saving}
                >
                  {t('assignmentManager.selectAll')}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleSelectNone}
                  disabled={saving}
                >
                  {t('assignmentManager.selectNone')}
                </button>
              </div>

              <div className="user-list">
                <div className="user-list-header">
                  <h4>{t('assignmentManager.students')}</h4>
                  <span className="list-info">{t('assignmentManager.selectStudentsToAssign')}</span>
                </div>

                <div className="user-grid">
                  {perTriar.map((user) => {
                    const isSelected = selectedUsers.has(user.id);
                    const wasOriginallyAssigned = assignments.assigned_users.some(u => u.id === user.id);

                    return (
                      <div
                        key={user.id}
                        className={`user-item ${isSelected ? 'selected' : ''} ${wasOriginallyAssigned ? 'originally-assigned' : ''} ${user.es_equip ? 'user-item--equip' : ''}`}
                        onClick={() => handleUserToggle(user.id)}
                      >
                        <div className="user-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleUserToggle(user.id)}
                            disabled={saving}
                          />
                        </div>
                        <div className="user-info">
                          <span className="user-name">{user.display_name || user.username}</span>
                          {wasOriginallyAssigned && (
                            <span className="user-status">{t('assignmentManager.currentlyAssigned')}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {assignments.available_users.length === 0 && (
                  <div className="empty-state">
                    <p>{t('assignmentManager.noStudentsAvailable')}</p>
                  </div>
                )}
              </div>

              {hasChanges && (
                <div className="changes-summary">
                  <h4>{t('assignmentManager.changesPreview')}</h4>
                  {toAdd.length > 0 && (
                    <div className="change-item add">
                      <span className="change-label">{t('assignmentManager.willBeAssigned')} ({toAdd.length}):</span>
                      <div className="change-users">
                        {toAdd.map(userId => (
                          <span key={userId} className="change-user add">
                            +{nomDe(userId)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {toRemove.length > 0 && (
                    <div className="change-item remove">
                      <span className="change-label">{t('assignmentManager.willBeUnassigned')} ({toRemove.length}):</span>
                      <div className="change-users">
                        {toRemove.map(userId => (
                          <span key={userId} className="change-user remove">
                            -{nomDe(userId)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || loading || !hasChanges}
          >
            {saving ? t('assignmentManager.saving') : `${t('assignmentManager.saveChanges')}${hasChanges ? ` (${toAdd.length + toRemove.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssignmentManager;